import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp, LANGUAGES } from "@/context/AppContext";
import { api } from "@/lib/api";
import { publicOrigin } from "@/lib/publicOrigin";
import { sharedGet } from "@/lib/sharedGet";
import { FOLLOW_REQUESTS_EVENT, FOLLOW_REQUESTS_LINK } from "@/components/privacy/ProfilePrivacy";
import { shareLinkCard } from "@/lib/nativeShare";
import { resolveMediaUrl } from "@/components/FileUpload";
import { videoPosterFor } from "@/lib/neutralPoster";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { avatarSrc } from "@/lib/defaults";
import { Flag } from "@/components/Flag";
import { isDarkMode, toggleDarkMode } from "@/lib/theme";
import { hasAnyRole, adminConsoleLabel } from "@/lib/roles";
import { zoneKeys, accountMenuKeys } from "@/lib/layoutBlocks";
import { toast } from "sonner";
import {
  Home, Film, Church, Heart, Users, CalendarDays, ShoppingBag, Megaphone, Radio, Bookmark,
  PenSquare, Camera, SlidersHorizontal, Receipt, Moon, Sun, LogOut, Settings, LifeBuoy, Clock, Bell, Share2, HeartHandshake, BookOpen, X, UserPlus, UserCheck, Clapperboard, Library,
} from "lucide-react";

const fmtWhen = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang || "fr", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// Yalola modules shown in the mobile/tablet navigation drawer ("Explorer"), keyed by the
// SAME admin layout block keys as the web left sidebar (zone left_sidebar) → order and
// on/off set in Admin › Mise en page apply on mobile exactly like on the web.
// NOTE: Prières / Témoignages / Plan de lecture are intentionally NOT here on mobile —
// they live in the header "+" menu (admin-managed via "create_menu" layout blocks).
const MODULES = {
  nav_groups:      { key: "groups",      to: "/groups",      icon: Users,       tKey: "nav.groups",      fb: "Groupes",         color: "#2E7CF6", feature: "groups" },
  nav_events:      { key: "events",      to: "/events",      icon: CalendarDays,tKey: "nav.events",      fb: "Événements",      color: "#8B5CF6" },
  nav_marketplace: { key: "marketplace", to: "/marketplace", icon: ShoppingBag, tKey: "nav.marketplace", fb: "Marketplace",     color: "#2D9E68", feature: "marketplace" },
  nav_ads:         { key: "ads",         to: "/ads",         icon: Megaphone,   tKey: "nav.ads",         fb: "Publicités",      color: "#E0A63C", feature: "ads" },
  nav_saved:       { key: "saved",       to: "/saved",       icon: Bookmark,    tKey: "nav.saved",       fb: "Enregistrements", color: "#0D9488" },
};
const DRAWER_NAV_DEFAULT = ["nav_groups", "nav_events", "nav_marketplace", "nav_ads", "nav_saved"];

// "Create" shortcuts — Yalola features only. Reused by the header "+" menu too.
export const CREATE_ITEMS = [
  { key: "post",   to: "/home",             icon: PenSquare,    tKey: "create.post",   fb: "Publication" },
  { key: "testimony", to: "/testimonies",   icon: HeartHandshake, tKey: "create.testimony", fb: "Témoignage", feature: "testimonies" },
  { key: "story",  to: "/home",             icon: Camera,       tKey: "create.story",  fb: "Story",      feature: "stories" },
  { key: "reel",   to: "/reels",            icon: Film,         tKey: "create.reel",   fb: "Reel",       feature: "reels" },
  { key: "live",   to: "/live?create=1",    icon: Radio,        tKey: "create.live",   fb: "Direct",     feature: "live" },
  { key: "ad",     to: "/ads",              icon: Megaphone,    tKey: "create.ad",     fb: "Publicité",  feature: "ads" },
  { key: "event",  to: "/events",           icon: CalendarDays, tKey: "create.event",  fb: "Événement" },
  { key: "group",  to: "/groups",           icon: Users,        tKey: "create.group",  fb: "Groupe",     feature: "groups" },
  { key: "church", to: "/churches?suggest=1", icon: Church,     tKey: "create.church", fb: "Suggérer une église" },
];

// Items available in the header "+" menu (mobile / PWA only). Order & visibility are
// admin-managed via the "create_menu" layout blocks (Admin → Mise en page → Bouton +).
// Block keys are "create_<key>" (e.g. create_prayers → prayers).
export const PLUS_MENU_ITEMS = {
  prayers:     { to: "/prayers",            icon: HeartHandshake, tKey: "nav.prayers",     fb: "Prières",             feature: "prayers" },
  testimonies: { to: "/testimonies",        icon: HeartHandshake, tKey: "nav.testimonies", fb: "Témoignages",         feature: "testimonies" },
  reading:     { to: "/reading",            icon: BookOpen,       tKey: "nav.reading",     fb: "Plan de lecture",     feature: "reading" },
  live:        { to: "/live?create=1",      icon: Radio,          tKey: "create.live",     fb: "Direct",              feature: "live" },
  ad:          { to: "/ads",                icon: Megaphone,      tKey: "create.ad",       fb: "Publicité",           feature: "ads" },
  event:       { to: "/events",             icon: CalendarDays,   tKey: "create.event",    fb: "Événement" },
  church:      { to: "/churches?suggest=1", icon: Church,         tKey: "create.church",   fb: "Suggérer une église" },
};
export const PLUS_MENU_DEFAULT_ORDER = ["prayers", "testimonies", "reading", "live", "ad", "event", "church"];

const AccountRow = ({ to, onClick, icon: Icon, label, testId }) => {
  const cls = "w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)] text-left";
  const inner = (
    <>
      <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(0,0,0,0.06)" }}>
        <Icon size={17} style={{ color: "var(--yalola-text)" }} />
      </span>
      <span className="text-[14px]" style={{ color: "var(--yalola-text)" }}>{label}</span>
    </>
  );
  if (to) return <Link to={to} onClick={onClick} className={cls} data-testid={testId}>{inner}</Link>;
  return <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
};

export const NavigationDrawer = ({ open, onOpenChange }) => {
  const { user, logout, lang, setLang, t, config } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [dark, setDark] = React.useState(isDarkMode());
  const [nextLive, setNextLive] = React.useState(null);
  const [badges, setBadges] = React.useState(null);
  const [myChurches, setMyChurches] = React.useState([]);
  const [remindBusy, setRemindBusy] = React.useState(false);
  const features = config?.settings?.features || {};
  const fEnabled = (k) => features[k] !== false;
  const isAdmin = hasAnyRole(user, ["super_admin", "manager", "moderator", "associate", "support"]);
  const isHost = hasAnyRole(user, ["preacher", "artist", "influencer"]);
  const close = () => onOpenChange(false);

  React.useEffect(() => {
    if (!open || !user) return;
    api.get("/live/next-followed-church").then(({ data }) => setNextLive(data.live)).catch(() => {});
    sharedGet("/me/nav-badges", { ttl: 20000 }).then((data) => setBadges(data)).catch(() => {});
    const onRequests = (e) => { if (typeof e.detail?.count === "number") setBadges((b) => ({ ...(b || {}), follow_requests: e.detail.count })); };
    window.addEventListener(FOLLOW_REQUESTS_EVENT, onRequests);
    if (user.role === "preacher" && user.is_verified) {
      api.get("/churches/mine").then(({ data }) => setMyChurches(data.churches || [])).catch(() => setMyChurches([]));
    } else {
      setMyChurches([]);
    }
    return () => window.removeEventListener(FOLLOW_REQUESTS_EVENT, onRequests);
  }, [open, user]);
  const followRequests = badges?.follow_requests || 0;

  const moduleBadge = (key) => {
    if (!badges) return 0;
    if (key === "events") return badges.events_upcoming || 0;
    if (key === "live") return badges.live_now || 0;
    return 0;
  };

  const handleLogout = async () => { close(); await logout(); toast.success(t("nav.see_you_soon", "À bientôt !")); nav("/"); };

  const [bigScreen, setBigScreen] = React.useState(() => typeof window !== "undefined" && window.innerWidth >= 640);
  React.useEffect(() => {
    const on = () => setBigScreen(window.innerWidth >= 640);
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [open]);
  const coverVideoOn = config?.settings?.church?.cover_video !== false;
  const toggleRemind = async (e) => {
    e.stopPropagation();
    if (!nextLive || remindBusy) return;
    setRemindBusy(true);
    try {
      const { data } = await api.post(`/live/scheduled/${nextLive.id}/remind`);
      setNextLive((nl) => (nl ? { ...nl, reminded: data.reminded } : nl));
      toast.success(data.reminded ? t("live.reminded", "Rappel activé") : t("live.remind_off", "Rappel désactivé"));
    } catch { /* noop */ } finally { setRemindBusy(false); }
  };
  const inviteChurch = async (e) => {
    e.stopPropagation();
    if (!nextLive) return;
    const url = `${publicOrigin()}/live/scheduled/${nextLive.id}`;
    const title = nextLive.title || t("studio.default_title", "Direct Yalola");
    const text = `${nextLive.church?.name ? nextLive.church.name + " — " : ""}${title} 🙏`;
    await shareLinkCard({ title, text, url, copiedMessage: t("live.invite_copied", "Lien du direct copié 🔗") });
  };

  const swipe = React.useRef({ x: 0, y: 0, active: false });
  const onTouchStart = (e) => { const tch = e.touches[0]; swipe.current = { x: tch.clientX, y: tch.clientY, active: true }; };
  const onTouchMove = (e) => {
    if (!swipe.current.active) return;
    const tch = e.touches[0];
    const dx = swipe.current.x - tch.clientX;            // leftward drag distance
    const dy = Math.abs(tch.clientY - swipe.current.y);
    if (dx > 45 && dx > dy) { swipe.current.active = false; close(); }
  };
  const onTouchEnd = () => { swipe.current.active = false; };

  if (!user) return null;

  const modules = zoneKeys(config, "left_sidebar", DRAWER_NAV_DEFAULT)
    .filter((k) => MODULES[k])
    .map((k) => MODULES[k])
    .filter((m) => !m.feature || fEnabled(m.feature));
  // Tuiles « Créer » du tiroir : zone admin drawer_create (Direct + Reel par défaut), clés drawer_create_<key>.
  const creates = zoneKeys(config, "drawer_create", ["drawer_create_live", "drawer_create_reel"])
    .map((k) => CREATE_ITEMS.find((c) => c.key === k.replace(/^drawer_create_/, "")))
    .filter((c) => c && (!c.feature || fEnabled(c.feature)));

  // « Compte » : lignes pilotées par la zone admin account_menu (ordre + visibilité), comme le menu avatar web.
  const accountRow = (key) => {
    switch (key) {
      case "account_manage_church":
        if (myChurches.length === 1) return <AccountRow key={key} to={`/churches/${myChurches[0].id}`} onClick={close} icon={Church} label={t("menu.manage_church", "Gérer l'église")} testId="drawer-manage-church" />;
        return myChurches.length > 1 ? myChurches.map((c) => (
          <AccountRow key={c.id} to={`/churches/${c.id}`} onClick={close} icon={Church} label={`${t("menu.manage_church", "Gérer l'église")} · ${c.name}`} testId={`drawer-manage-church-${c.id}`} />
        )) : null;
      case "account_resources":
        return features.resources === true ? <AccountRow key={key} to="/ressources" onClick={close} icon={Library} label={t("nav.resources", "Ressources")} testId="drawer-resources" /> : null;
      case "account_settings":
        return <AccountRow key={key} to="/settings" onClick={close} icon={SlidersHorizontal} label={t("menu.settings", "Paramètres")} testId="drawer-settings" />;
      case "account_my_lives":
        return isHost ? <AccountRow key={key} to="/mes-directs" onClick={close} icon={Clapperboard} label={t("menu.my_lives", "Mes directs")} testId="drawer-my-lives" /> : null;
      case "account_invite":
        return <AccountRow key={key} to="/invite" onClick={close} icon={UserPlus} label={t("invite.menu", "Inviter des amis")} testId="drawer-invite-friends" />;
      case "account_finances":
        return <AccountRow key={key} to={`/profile/${user.id}?tab=espace#paiements-finances`} onClick={close} icon={Receipt} label={t("menu.history", "Paiements & finances")} testId="drawer-history" />;
      case "account_support":
        return <AccountRow key={key} to="/support" onClick={close} icon={LifeBuoy} label={t("menu.support", "Support")} testId="drawer-support" />;
      case "account_admin":
        return isAdmin ? <AccountRow key={key} to="/admin" onClick={close} icon={Settings} label={adminConsoleLabel(user, t)} testId="drawer-admin" /> : null;
      case "account_dark_mode":
        return <AccountRow key={key} onClick={() => { toggleDarkMode(); setDark(isDarkMode()); }} icon={dark ? Sun : Moon} label={dark ? t("menu.light", "Mode clair") : t("menu.dark", "Mode sombre")} testId="drawer-dark-toggle" />;
      default:
        return null;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        hideClose
        className="w-[88%] max-w-[380px] p-0 flex flex-col border-r"
        style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}
        data-testid="mobile-nav-drawer"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <SheetHeader className="px-4 pt-4 pb-2 text-left" style={{ paddingTop: "calc(var(--sa-top) + 1rem)" }}>
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="font-display text-lg">{t("nav.menu", "Menu")}</SheetTitle>
            <button
              onClick={close}
              aria-label={t("common.close", "Fermer")}
              className="h-9 w-9 -mr-1 rounded-full grid place-items-center shrink-0 transition-colors hover:bg-[rgba(0,0,0,0.08)] active:scale-95"
              style={{ background: "rgba(0,0,0,0.05)" }}
              data-testid="drawer-close"
            >
              <X size={18} style={{ color: "var(--yalola-text)" }} />
            </button>
          </div>
          <SheetDescription className="sr-only">{t("nav.menu", "Menu")}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-3 pb-8">
          {/* Next scheduled live from a followed church */}
          {nextLive && nextLive.church && (
            <div
              className="rounded-2xl overflow-hidden mb-3 border"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid="drawer-next-live"
            >
              <button
                onClick={() => { close(); nav(`/live/scheduled/${nextLive.id}`); }}
                className="block w-full text-left transition-transform active:scale-[0.98]"
                data-testid="drawer-next-live-open"
              >
                <div className="relative h-24">
                  {coverVideoOn && bigScreen && nextLive.church.cover_video_url ? (
                    <video
                      src={resolveMediaUrl(nextLive.church.cover_video_url)}
                      poster={videoPosterFor(resolveMediaUrl(nextLive.church.cover_video_url), nextLive.church.cover_url ? resolveMediaUrl(nextLive.church.cover_url) : null)}
                      autoPlay loop muted playsInline preload="metadata"
                      className="absolute inset-0 w-full h-full object-cover"
                      data-testid="drawer-next-live-video"
                    />
                  ) : nextLive.church.cover_url || nextLive.thumbnail_url || nextLive.church.logo_url ? (
                    <img src={resolveMediaUrl(nextLive.church.cover_url || nextLive.thumbnail_url || nextLive.church.logo_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0" style={{ background: "var(--yalola-secondary)" }} />
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.05))" }} />
                  <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full" style={{ background: "var(--yalola-primary)" }}>
                    <Radio size={11} /> {t("live.next_reminder", "Prochain direct")}
                  </div>
                  <div className="absolute bottom-2 left-2.5 right-2.5">
                    <div className="text-white font-bold text-sm truncate font-display">{nextLive.church.name}</div>
                  </div>
                </div>
              </button>
              <div className="px-3 py-2.5 space-y-2" style={{ background: "var(--yalola-surface-alt)" }}>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate">{nextLive.title}</div>
                  <div className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
                    <Clock size={12} /> {fmtWhen(nextLive.scheduled_at, lang)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleRemind}
                    disabled={remindBusy}
                    className="flex-1 flex items-center justify-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-full transition-transform active:scale-95 disabled:opacity-60"
                    style={nextLive.reminded
                      ? { background: "var(--yalola-primary)", color: "#fff" }
                      : { background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}
                    data-testid="drawer-remind-btn"
                  >
                    <Bell size={13} /> {nextLive.reminded ? t("live.reminded", "Rappel activé") : t("live.remind_me", "Me rappeler")}
                  </button>
                  <button
                    onClick={inviteChurch}
                    className="flex-1 flex items-center justify-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-full transition-transform active:scale-95"
                    style={{ background: "var(--yalola-secondary)", color: "#fff" }}
                    data-testid="drawer-invite-church-btn"
                  >
                    <Share2 size={13} /> {t("live.invite_church", "Inviter mon église")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Profile */}
          <Link
            to={`/profile/${user.id}`}
            onClick={close}
            className="flex items-center gap-3 px-2 py-2.5 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
            data-testid="drawer-profile-card"
          >
            <span className="relative shrink-0">
              <Avatar className="h-11 w-11">
                <AvatarImage src={avatarSrc(user)} />
                <AvatarFallback style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", fontWeight: 700 }}>{user.name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              {followRequests > 0 && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2" style={{ background: "#DC2626", borderColor: "var(--yalola-surface)" }} data-testid="drawer-avatar-requests-dot" />}
            </span>
            <div className="min-w-0">
              <div className="font-semibold text-[15px] truncate flex items-center gap-1">{user.name}{user.is_verified && <VerifiedBadge user={user} size={13} />}</div>
              <div className="text-[12px]" style={{ color: "var(--yalola-text-muted)" }}>{t("nav.my_profile", "Voir mon profil")}</div>
            </div>
          </Link>
          {followRequests > 0 && (
            <Link
              to={FOLLOW_REQUESTS_LINK}
              onClick={close}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-transform active:scale-95"
              style={{ background: "rgba(220,38,38,0.08)", color: "#B91C1C" }}
              data-testid="drawer-follow-requests"
              data-count={followRequests}
            >
              <UserCheck size={17} />
              <span className="text-[13px] font-semibold flex-1">{t("profile.follow_requests_btn", "Demandes ({n})").replace("{n}", String(followRequests))}</span>
              <span className="min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-bold text-white" style={{ background: "#DC2626" }}>{followRequests > 9 ? "9+" : followRequests}</span>
            </Link>
          )}

          {/* Créer */}
          <div className="mt-3">
            <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--yalola-text-muted)" }}>{t("nav.create", "Créer")}</div>
            <div className="grid grid-cols-2 gap-2" data-testid="drawer-create-section">
              {creates.map((c) => (
                <Link
                  key={c.key}
                  to={c.to}
                  onClick={close}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-transform active:scale-95"
                  style={{ background: "var(--yalola-surface-alt)" }}
                  data-testid={`drawer-create-${c.key}`}
                >
                  <c.icon size={17} style={{ color: "var(--yalola-primary)" }} />
                  <span className="text-[13px] font-medium truncate">{t(c.tKey, c.fb)}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Explorer */}
          <div className="mt-4">
            <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--yalola-text-muted)" }}>{t("nav.explore", "Explorer")}</div>
            {modules.map((m) => {
              const active = loc.pathname === m.to || (m.to !== "/home" && loc.pathname.startsWith(m.to));
              return (
                <Link
                  key={m.key}
                  to={m.to}
                  onClick={close}
                  className="flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
                  style={{ background: active ? "rgba(0,0,0,0.06)" : "transparent" }}
                  data-testid={`drawer-nav-${m.key}`}
                >
                  <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: m.color + "1f" }}>
                    <m.icon size={18} color={m.color} strokeWidth={2.1} />
                  </span>
                  <span className="text-[14px] flex-1" style={{ color: "var(--yalola-text)", fontWeight: active ? 700 : 500 }}>{t(m.tKey, m.fb)}</span>
                  {moduleBadge(m.key) > 0 && (
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-bold text-white"
                      style={{ background: m.key === "live" ? "#DC2626" : "var(--yalola-primary)" }}
                      data-testid={`drawer-badge-${m.key}`}
                    >
                      {moduleBadge(m.key) > 9 ? "9+" : moduleBadge(m.key)}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Compte */}
          <div className="mt-4 pt-3 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--yalola-text-muted)" }}>{t("nav.account", "Compte")}</div>
            {accountMenuKeys(config).map(accountRow)}

            <div className="px-2 mt-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--yalola-text-muted)" }}>{t("menu.language", "Langue")}</div>
              <div className="grid grid-cols-4 gap-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`py-1.5 rounded-lg text-xs ${lang === l.code ? "font-bold" : ""}`}
                    style={{ background: lang === l.code ? "var(--yalola-primary-soft)" : "var(--yalola-surface-alt)", color: lang === l.code ? "var(--yalola-primary)" : "var(--yalola-text)" }}
                    data-testid={`drawer-lang-${l.code}`}
                  >
                    <span className="flex flex-col items-center gap-0.5"><Flag code={l.code} size={16} /><span className="uppercase">{l.code}</span></span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-2">
              <AccountRow onClick={handleLogout} icon={LogOut} label={t("menu.logout", "Déconnexion")} testId="drawer-logout" />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
